import { FC, memo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Box, Grid } from '@mui/material';

import { FieldArrayComponent } from 'components/_basic/Form/FieldArray';
import { TextInputControl } from 'components/_basic/Form/TextInput';
import { SelectComponentControl } from 'components/_basic/SelectComponent/SelectComponentControl/SelectComponentControl';
import { ISectionRowProps as IProps } from 'components/BusinessToolForm';
import { CONSUMER_OPTIONS_DEU } from 'components/BusinessToolForm/constants/deu/options_deu';
import { CONSUMER_OPTIONS_ES } from 'components/BusinessToolForm/constants/es/options_es';
import { CONSUMER_OPTIONS, UNITS_VALUES } from 'components/BusinessToolForm/constants/options';
import { CONSUMER_OPTIONS_UA } from 'components/BusinessToolForm/constants/ua/options_ua';

import { concatWithDot } from 'helpers/string';

import { ConsumerDetails } from './components';
import {
  consumerDetailsInitialValues,
  CURRENT_MARKET_STRUCTURE_NAMES,
  MARKET_ANALYSIS_MAX_LENGTH,
  UNITS_NAMES_STRUCTURE,
  UnitValues,
} from '../../utils/form';
import { MonthYearDatepicker } from '../MonthYearDatePicker';
import { UnitsPicker } from '../UnitsPicker';

import styles from './CurrentMarketStructure.module.scss';

export const CurrentMarketStructure: FC<IProps> = memo(({ name, disabled = false }) => {
  const { control, getValues } = useFormContext();
  const { t, i18n } = useTranslation();

  const getChosenConsumerOptions = (lang: string) => {
    switch (lang) {
      case 'de':
        return CONSUMER_OPTIONS_DEU;

      case 'uk':
        return CONSUMER_OPTIONS_UA;

      case 'es':
        return CONSUMER_OPTIONS_ES;

      default:
        return CONSUMER_OPTIONS;
    }
  };

  const chosenUnits = getValues(
    concatWithDot(name, CURRENT_MARKET_STRUCTURE_NAMES.UNITS, UNITS_NAMES_STRUCTURE.SELECTED_UNITS),
  ) as UnitValues;

  const getUnitValue = (unitType: string) => {
    return getValues(concatWithDot(name, CURRENT_MARKET_STRUCTURE_NAMES.UNITS, unitType)) as UnitValues;
  };

  const unitValue = getUnitValue(
    chosenUnits === UNITS_VALUES.VOLUME_IN ? UNITS_NAMES_STRUCTURE.VOLUME : UNITS_NAMES_STRUCTURE.CURRENCY,
  );

  const selectedTitle = getValues(concatWithDot(name, CURRENT_MARKET_STRUCTURE_NAMES.CONSUMER));

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <TextInputControl
          control={control}
          id={concatWithDot(name, CURRENT_MARKET_STRUCTURE_NAMES.TITLE)}
          name={concatWithDot(name, CURRENT_MARKET_STRUCTURE_NAMES.TITLE)}
          label={t('chartTitle')}
          disabled={disabled}
          maxLength={150}
        />
      </Grid>
      <Grid item xs={12} className={styles.formContainer}>
        <>
          <Box className={styles.selectContainer}>
            <SelectComponentControl
              control={control}
              id={concatWithDot(name, CURRENT_MARKET_STRUCTURE_NAMES.CONSUMER)}
              name={concatWithDot(name, CURRENT_MARKET_STRUCTURE_NAMES.CONSUMER)}
              disabled={disabled}
              options={getChosenConsumerOptions(i18n.language)}
              sx={{ marginRight: '16px' }}
              defaultValue={getChosenConsumerOptions(i18n.language)[2].value}
            />
            <MonthYearDatepicker name={concatWithDot(name, CURRENT_MARKET_STRUCTURE_NAMES.DATE)} disabled={disabled} />
          </Box>
        </>
      </Grid>
      <Grid item>
        <UnitsPicker name={concatWithDot(name, CURRENT_MARKET_STRUCTURE_NAMES.UNITS)} disabled={disabled} />
      </Grid>
      <Grid item xs={12}>
        <FieldArrayComponent
          name={concatWithDot(name, CURRENT_MARKET_STRUCTURE_NAMES.CONSUMER_DETAILS)}
          initialItem={consumerDetailsInitialValues}
          buttonText={t(`marketAnalysisForm.overview.currentMarketStructureTable.${selectedTitle}`)}
          disabled={disabled}
        >
          {(namePrefix, index) => (
            <ConsumerDetails
              name={namePrefix}
              index={index}
              disabled={disabled}
              chosenUnits={unitValue}
              title={t(`marketAnalysisForm.overview.currentMarketStructureTable.${selectedTitle}`)}
            />
          )}
        </FieldArrayComponent>
      </Grid>
      <Grid item xs={12}>
        <TextInputControl
          control={control}
          id={concatWithDot(name, CURRENT_MARKET_STRUCTURE_NAMES.SOURCE)}
          name={concatWithDot(name, CURRENT_MARKET_STRUCTURE_NAMES.SOURCE)}
          label={t('dataSource')}
          disabled={disabled}
          maxLength={MARKET_ANALYSIS_MAX_LENGTH.CURRENT_MARKET_STRUCTURE.DATA_SOURCE}
        />
      </Grid>
    </Grid>
  );
});
